import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BackButton from "../Navigation/BackButton";
import "./timesheet-detail.style.css";
import { isOwnerOrFinance } from "../../utils/role-check";
import Loader from "../Loader/Loader";
import NoDataConstruction from "../EmptyStates/NoDataConstruction";

function TimesheetDetailView({
    rateType,
    timesheet,
    loading,
    error,
    materialsTotal,
    daysOrder,
    daysTranslated,
    statusTranslated,
    extraWorkItems,
    materials,
    handleGeneratePDF,
    projectRole,
    auth,
    handleApprove,
    handleReject
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Helper to construct the correct file URL
    const getTimesheetFileUrl = (path) => {
        if (typeof path !== "string" || !path) return "";
        if (path.startsWith("http")) return path;

        // Remove leading slash if present
        let cleanPath = path.startsWith("/") ? path.slice(1) : path;

        // Use env or fallback
        const baseUrl = (window.env && window.env.REACT_APP_BASE_URL) || process.env.REACT_APP_BASE_URL || "http://localhost:8080";

        // If the path already starts with "timesheets/", construct the proper URL
        if (cleanPath.startsWith("timesheets/")) {
            // Path format: timesheets/2/filename.png
            // Need to convert to: /timesheets/2/filename.png for the API route
            return `${baseUrl}/${cleanPath}`;
        }

        // If it starts with "uploads/", use as-is
        if (cleanPath.startsWith("uploads/")) {
            return `${baseUrl}/${cleanPath}`;
        }

        // Otherwise, prepend "uploads/"
        return `${baseUrl}/uploads/${cleanPath}`;
    };

    if (loading) {
        return <Loader message={t('common.loading')} />;
    }
    if (error) {
        return <NoDataConstruction text={t('common.error')}/>;
    }
    if (!timesheet) {
        return <NoDataConstruction text={t('timesheet.timesheetNotFound')}/>;
    }
    return (
        <div className="timesheet-container timesheet-detail--card">
            <BackButton label={t('timesheet.backToTimesheets')} />
            <div className="timesheet-header timesheet-header--card">
                <h2>{t('timesheet.timesheetDetail')}</h2>
                <div className="meta-info meta-info--card">
                    <div><strong>{t('timesheet.employee')}:</strong> {timesheet.user?.name}</div>
                    <div><strong>{t('timesheet.project')}:</strong> {timesheet.project?.name}</div>
                    <div><strong>{t('timesheet.location')}:</strong> {timesheet.project?.location}</div>
                    <div><strong>{t('timesheet.week')}:</strong> {timesheet.week_number} ({timesheet.year})</div>
                    <div className={`status-badge ${timesheet.status}`}>{statusTranslated[timesheet.status]}</div>
                </div>
            </div>
            {(rateType === "meter" ? timesheet.meters_worked : timesheet.hours_worked) && (
                <div className="timesheet-section timesheet-section--card">
                    <div className="timesheet-info">
                        <h3>{rateType === "meter" ? t('timesheet.metersPerDay') : t('timesheet.hoursPerDay')}</h3>
                        <table className="timesheet-table">
                            <thead>
                                <tr>
                                    {daysOrder.map(day => (
                                        <th key={day}>{daysTranslated[day]}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    {daysOrder.map(day => {
                                        const value = rateType === "meter"
                                            ? (timesheet.meters_worked?.[day] || 0)
                                            : (timesheet.hours_worked?.[day] || 0);
                                        return (
                                            <td key={day} data-label={daysTranslated[day]}>{value}</td>
                                        );
                                    })}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            <div className="timesheet-section timesheet-section--card">
                <h3>{t('timesheet.extraWork')}</h3>
                {(!Array.isArray(extraWorkItems) || extraWorkItems.length === 0) ? (
                    <p>{t('timesheet.noExtraWorkAdded')}</p>
                ) : (
                    <div className="table-wrapper">
                        <table className="extra-work-table">
                            <thead>
                                <tr>
                                    <th>{t('timesheet.description')}</th>
                                    <th>{t('timesheet.notes')}</th>
                                    <th>{t('timesheet.photo')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {extraWorkItems.map((item, idx) => (
                                    <tr key={idx}>
                                        <td data-label="Omschrijving">{item.omschrijving || "Geen omschrijving"}</td>
                                        <td data-label="Opmerkingen">{item.opmerkingen || "Geen opmerkingen"}</td>
                                        <td data-label="Foto">
                                            {Array.isArray(item.photos) && item.photos.length > 0 ? (
                                                item.photos.map((url, i) => (
                                                    <a key={i} href={getTimesheetFileUrl(url)} target="_blank" rel="noopener noreferrer">
                                                        <img src={getTimesheetFileUrl(url)} alt="Meerwerk foto" className="meerwerk-foto" />
                                                    </a>
                                                ))
                                            ) : (
                                                <span>Geen foto</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            <div className="timesheet-section timesheet-section--card">
                <h3>{t('timesheet.materials')}</h3>
                {(!Array.isArray(materials) || materials.length === 0) ? (
                    <p>{t('timesheet.noMaterialsAdded')}</p>
                ) : (
                    <div className="table-wrapper">
                        <table className="materials-table">
                            <thead>
                                <tr>
                                    <th>{t('timesheet.brand')}</th>
                                    <th>{t('timesheet.materialName')}</th>
                                    <th>{t('timesheet.quantity')}</th>
                                    <th>{t('timesheet.unit')}</th>
                                    <th>{t('timesheet.costs')} (&euro;)</th>
                                    <th>{t('timesheet.receipt')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {materials.map((m, i) => (
                                    <tr key={i}>
                                        <td data-label={t('timesheet.brand')}>{m.name}</td>
                                        <td data-label={t('timesheet.materialName')}>{m.description}</td>
                                        <td data-label={t('timesheet.quantity')}>{m.quantity}</td>
                                        <td data-label={t('timesheet.unit')}>{m.unit.charAt(0).toUpperCase() + m.unit.slice(1)}</td>
                                        <td data-label={t('timesheet.costs')}>{m.cost}</td>
                                        <td data-label={t('timesheet.receipt')}>
                                            {Array.isArray(m.receipts) && m.receipts.length > 0
                                                ? m.receipts.map((receipt, idx) => (
                                                    <span key={idx}>
                                                        <a href={getTimesheetFileUrl(receipt)} target="_blank" rel="noopener noreferrer">{t('common.view')}</a>{" "}
                                                    </span>
                                                ))
                                                : m.receipts && (
                                                    <a href={getTimesheetFileUrl(m.receipts)} target="_blank" rel="noopener noreferrer">{t('common.view')}</a>
                                                )}
                                        </td>
                                    </tr>
                                ))}
                                <tr>
                                    <td colSpan={6} style={{ textAlign: "left", fontWeight: "bold" }}>
                                        {t('timesheet.totalMaterialCosts')}: &euro;{materialsTotal.toFixed(2)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            <div className="timesheet-section timesheet-section--card">
                <button type="button" className="btn-primary" onClick={handleGeneratePDF}>{t('timesheet.downloadPDF')}</button>

                {/* Edit button for draft timesheets - only shown to the owner of the timesheet */}
                {timesheet.status === "draft" && timesheet.user_id === auth?.userId && (
                    <button
                        type="button"
                        className="btn-edit-draft"
                        onClick={() => navigate(`/timesheets/${timesheet.id}/edit`)}
                        style={{ marginTop: 16 }}
                    >
                        {t('common.edit')}
                    </button>
                )}

                {(((projectRole === "voorman") && timesheet.status === "pending") ||
                    (isOwnerOrFinance(auth?.role) && ["pending", "approved_by_voorman"].includes(timesheet.status))) && (
                    <div style={{ marginBottom: 16, marginTop: 16, display: "flex", gap: 8 }}>
                        <button type="button" className="btn-approve full-width" onClick={() => handleApprove(timesheet.id)}>{t('timesheet.approve')}</button>
                        <button type="button" className="btn-delete full-width" onClick={() => handleReject(timesheet.id)}>{t('timesheet.reject')}</button>
                    </div>
                )}
            </div>
        </div>
    );
}

TimesheetDetailView.propTypes = {
    rateType: PropTypes.string.isRequired,
    timesheet: PropTypes.shape({
        id: PropTypes.number,
        user: PropTypes.shape({
            name: PropTypes.string
        }),
        project: PropTypes.shape({
            name: PropTypes.string,
            location: PropTypes.string
        }),
        week_number: PropTypes.number,
        year: PropTypes.number,
        status: PropTypes.string,
        hours_worked: PropTypes.object,
        meters_worked: PropTypes.object
    }),
    loading: PropTypes.bool.isRequired,
    error: PropTypes.string,
    materialsTotal: PropTypes.number.isRequired,
    daysOrder: PropTypes.arrayOf(PropTypes.string).isRequired,
    daysTranslated: PropTypes.object.isRequired,
    statusTranslated: PropTypes.object.isRequired,
    extraWorkItems: PropTypes.arrayOf(PropTypes.shape({
        omschrijving: PropTypes.string,
        opmerkingen: PropTypes.string,
        photos: PropTypes.arrayOf(PropTypes.string)
    })),
    materials: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        description: PropTypes.string,
        quantity: PropTypes.number,
        unit: PropTypes.string,
        cost: PropTypes.number,
        receipts: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.arrayOf(PropTypes.string)
        ])
    })),
    handleGeneratePDF: PropTypes.func.isRequired,
    projectRole: PropTypes.string,
    auth: PropTypes.shape({
        role: PropTypes.string,
        userId: PropTypes.number
    }),
    handleApprove: PropTypes.func.isRequired,
    handleReject: PropTypes.func.isRequired
};

TimesheetDetailView.defaultProps = {
    timesheet: null,
    error: null,
    projectRole: null,
    extraWorkItems: [],
    materials: [],
    auth: null
};

export default TimesheetDetailView;
